import React, { useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const getImage = (post) =>
  post?.featuredImage
    ? `${process.env.REACT_APP_DO_SPACES_URL}${post.featuredImage}`
    : "/placeholder.jpg";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "No date";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const RelatedPosts = ({ posts = [], authorPosts = [] }) => {
  const [activeTab, setActiveTab] = useState("related");

  const CustomPrevArrow = ({ onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-2 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-sm bg-white text-gray-600 shadow-sm ring-1 ring-slate-200 transition hover:text-[rgb(126,34,206)] hover:ring-[rgba(126,34,206,0.16)]"
      aria-label="Previous posts"
    >
      <ArrowLeftIcon className="h-5 w-5" />
    </button>
  );

  const CustomNextArrow = ({ onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="absolute right-2 top-1/2 z-10 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-sm bg-white text-gray-600 shadow-sm ring-1 ring-slate-200 transition hover:text-[rgb(126,34,206)] hover:ring-[rgba(126,34,206,0.16)]"
      aria-label="Next posts"
    >
      <ArrowRightIcon className="h-5 w-5" />
    </button>
  );

  const relatedSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  const authorSliderSettings = {
    ...relatedSliderSettings,
    slidesToShow: 3,
  };

  const renderSlider = (items, settings) => {
    if (!items.length) {
      return (
        <div className="rounded-md border border-dashed border-slate-200 bg-white p-8 text-center text-sm font-medium text-gray-500">
          No posts available yet.
        </div>
      );
    }

    return (
      <Slider {...settings}>
        {items.map((post, index) => (
          <div key={index} className="p-3">
            <article className="group overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-[rgba(126,34,206,0.28)] hover:shadow-md">
              <a href={post.link} className="block overflow-hidden bg-slate-100">
                <img
                  src={getImage(post)}
                  alt={post.title}
                  className="h-48 w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </a>
              <div className="p-4">
                <h3 className="line-clamp-2 text-lg font-bold leading-6 text-gray-900">
                  <a href={post.link} className="hover:text-[rgb(126,34,206)]">
                    {post.title}
                  </a>
                </h3>
                <p className="mt-3 text-sm font-medium text-gray-500">
                  {post.author || "PrepNexis"} / {formatDate(post.date)}
                </p>
              </div>
            </article>
          </div>
        ))}
      </Slider>
    );
  };

  return (
    <section className="mx-auto max-w-7xl px-4 pb-12 pt-4 sm:px-6 lg:px-8">
      <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="mb-5 flex flex-col gap-3 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase text-[rgb(126,34,206)]">
              Keep reading
            </p>
            <h2 className="mt-1 text-2xl font-extrabold text-gray-900">
              More from the blog
            </h2>
          </div>
          <div className="flex rounded-sm bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => setActiveTab("related")}
              className={`rounded-sm px-4 py-2 text-sm font-semibold transition ${
                activeTab === "related"
                  ? "bg-white text-[rgb(126,34,206)] shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              Related posts
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("author")}
              className={`rounded-sm px-4 py-2 text-sm font-semibold transition ${
                activeTab === "author"
                  ? "bg-white text-[rgb(126,34,206)] shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              More from author
            </button>
          </div>
        </div>

        {activeTab === "related"
          ? renderSlider(posts, relatedSliderSettings)
          : renderSlider(authorPosts, authorSliderSettings)}
      </div>
    </section>
  );
};

export default RelatedPosts;
